import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * RL-513 / TRS §22.9 — the Returns report: refund receipts with the cashier
 * who rang them up and the one who approved them, and per-cashier totals.
 * Amounts are integer minor units as strings.
 */

export type ReturnsReportPerson = {
  cashierId: string | null;
  employeeCode: string | null;
  displayName: string | null;
};

export type ReturnsReportRow = {
  receiptId: string;
  finalisedAt: string;
  locationId: string | null;
  locationCode: string | null;
  terminalId: string;
  terminalCode: string;
  zReportRef: string | null;
  fiscalNumber: string | null;
  originalReceiptId: string | null;
  refundTotalMinor: string;
  currency: string | null;
  tender: string | null;
  returnReason: string | null;
  operator: ReturnsReportPerson | null;
  approver: ReturnsReportPerson | null;
  approvedByOther: boolean;
};

export type ReturnsReportCashierRow = ReturnsReportPerson & {
  currency: string | null;
  refundsOperatedCount: number;
  refundsOperatedTotalMinor: string;
  approvedByOtherCount: number;
  approvedByOtherTotalMinor: string;
  approvedForOthersCount: number;
  approvedForOthersTotalMinor: string;
};

export type ReturnsReport = {
  from: string;
  to: string;
  approversRecordedSince: string | null;
  totals: { currency: string | null; count: number; totalMinor: string }[];
  rows: ReturnsReportRow[];
  cashiers: ReturnsReportCashierRow[];
};

export type ReturnsReportFilters = {
  locations: { id: string; code: string; name: string }[];
  terminals: { id: string; code: string; displayName: string; locationId: string | null }[];
  cashiers: { id: string; employeeCode: string; displayName: string }[];
};

/** The report's scope: `from` inclusive, `to` exclusive (instants), at most 31 days. */
export type ReturnsReportScope = {
  from: string;
  to: string;
  locationId?: string;
  terminalId?: string;
  cashierId?: string;
};

/** The longest range the server accepts (as the fiscal journal). */
export const RETURNS_REPORT_MAX_DAYS = 31;

function scopeParams(scope: ReturnsReportScope): Record<string, string> {
  const params: Record<string, string> = { from: scope.from, to: scope.to };
  if (scope.locationId) params.locationId = scope.locationId;
  if (scope.terminalId) params.terminalId = scope.terminalId;
  if (scope.cashierId) params.cashierId = scope.cashierId;
  return params;
}

export function useReturnsReport(scope: ReturnsReportScope | null) {
  return useQuery({
    queryKey: ['reports', 'returns', scope] as const,
    enabled: scope !== null,
    queryFn: async (): Promise<ReturnsReport> =>
      (
        await apiClient.get<ReturnsReport>('/reports/returns', {
          params: scopeParams(scope as ReturnsReportScope),
        })
      ).data,
  });
}

export function useReturnsReportFilters() {
  return useQuery({
    queryKey: ['reports', 'returns', 'filters'] as const,
    queryFn: async (): Promise<ReturnsReportFilters> =>
      (await apiClient.get<ReturnsReportFilters>('/reports/returns/filters')).data,
    staleTime: 60_000,
  });
}

/** Download one of the two CSV files under the given name. */
export async function downloadReturnsCsv(
  scope: ReturnsReportScope,
  view: 'refunds' | 'cashiers',
  fileName: string,
): Promise<void> {
  const res = await apiClient.get<Blob>('/reports/returns/export', {
    params: { ...scopeParams(scope), view },
    responseType: 'blob',
    timeout: 120_000,
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
